import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOrganization } from "@/features/auth/services/organization.service";
import type { Organization } from "@/features/auth/types/organization.type";

type UseOrganizationQueryProps = {
  enabled: boolean
  organizationId: string | null
  accessToken: string | null
  onGetOrganizationSuccess: (organization: Organization) => void
  onGetOrganizationError: () => void
};

export const useOrganizationQuery = ({
  enabled, organizationId, accessToken, onGetOrganizationSuccess, onGetOrganizationError,
}: UseOrganizationQueryProps) => {
  const query = useQuery<Organization>({
    queryKey: ["organization", organizationId],
    queryFn: () => getOrganization(organizationId ?? "", accessToken ?? ""),
    enabled: enabled && !!accessToken && !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes - keep data fresh during navigation
    gcTime: 10 * 60 * 1000, // 10 minutes - prevent garbage collection during active sessions
  });

  useEffect(() => {
    if (query.isSuccess && query.data) {
      onGetOrganizationSuccess(query.data);
    }
  }, [query.isSuccess, query.data, onGetOrganizationSuccess]);

  useEffect(() => {
    if (query.isError) {
      onGetOrganizationError();
    }
  }, [query.isError, onGetOrganizationError]);

  return query;
};
